import { Head, Link } from "@inertiajs/react"
import { Bookmark } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ListShell } from "@/components/admin/list-shell"
import { SegmentDeleteDialog } from "@/components/crew/segment-delete-dialog"
import { SegmentRelativeDate } from "@/components/crew/segment-relative-date"
import { SegmentRenamePopover } from "@/components/crew/segment-rename-popover"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useIsMobile } from "@/hooks/use-mobile"
import AppLayout from "@/layouts/app-layout"
import { crewPath, crewSegmentsPath } from "@/routes"
import type {
  CrewSegmentRow,
  CrewSegmentRules,
  CrewSegmentsIndexProps,
  MasterclassCompletionLabel,
} from "@/types/crew-segments"

/**
 * OKS-28 — Crew Segments management page (`/crew/segments`).
 *
 * Sibling to `Brand/Crew/Index` — renders the brand's saved segments
 * as a table with rename + delete affordances per row, or an empty
 * card when the brand has zero segments.
 *
 * **5-state coverage (UX Spec §3):**
 *   - Loading — Inertia handles the partial-reload skeleton at the
 *     navigation layer (no in-page skeleton needed; the page is
 *     navigated to, not a partial reload of itself).
 *   - Empty — `is_empty: true` → empty `<Card>` with CTA to /crew.
 *   - Error — handled by Inertia's `errors` flash + the page reload.
 *   - Success — populated table.
 *   - Edge — long names truncate (CSS `truncate`) with `title` attr
 *     fallback for the full name.
 *
 * **Locked testid:** `crew-segments-page` (`data-empty="true|false"`).
 */
export default function CrewSegmentsIndex({
  crew_segments,
  is_empty,
}: CrewSegmentsIndexProps) {
  const { t } = useTranslation()
  // OKS-28 §5 (responsive) — <640px collapses the Table into a stacked
  // card list with action icons inline-right per card. `useIsMobile`
  // gates the JSX (rather than CSS-only) so the rename + delete
  // components can keep their locked `crew-segments-row-*` testids
  // without duplicating in the DOM.
  const isMobile = useIsMobile()

  return (
    <AppLayout>
      <Head title={t("crew_segments.manage.head_title")} />
      <div
        data-testid="crew-segments-page"
        data-empty={is_empty ? "true" : "false"}
      >
        <ListShell
          testId="crew-segments-shell"
          title={t("crew_segments.manage.heading")}
          subtitle={
            <p className="text-muted-foreground text-sm">
              {t("crew_segments.manage.subtitle")}
            </p>
          }
        >
          {is_empty ? (
            <CrewSegmentsEmptyCard />
          ) : isMobile ? (
            <CrewSegmentsCardList segments={crew_segments} />
          ) : (
            <Table data-testid="crew-segments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("crew_segments.manage.column.name")}</TableHead>
                  <TableHead className="w-24 text-right">
                    {t("crew_segments.manage.column.members")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t("crew_segments.manage.column.rules")}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {t("crew_segments.manage.column.created_by")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t("crew_segments.manage.column.created_at")}
                  </TableHead>
                  <TableHead className="w-24 text-right">
                    <span className="sr-only">
                      {t("crew_segments.manage.column.actions")}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {crew_segments.map((segment) => (
                  <CrewSegmentsRow key={segment.id} segment={segment} />
                ))}
              </TableBody>
            </Table>
          )}
        </ListShell>
      </div>
    </AppLayout>
  )
}

/**
 * OKS-28 §5 — mobile card-stack replacing the Table at <640px. Each
 * card surfaces: segment name (large, links to `/crew?segment=<id>`
 * exactly like the table row's name link), member count (muted),
 * created-by, and the rename + delete action icons inline-right.
 *
 * Reuses `<SegmentRenamePopover>` + `<SegmentDeleteDialog>` so the
 * locked `crew-segments-row-rename-trigger-{id}` /
 * `crew-segments-row-delete-trigger-{id}` testids stay consistent
 * across breakpoints (LQE system specs hit them either way).
 */
function CrewSegmentsCardList({ segments }: { segments: CrewSegmentRow[] }) {
  return (
    <div data-testid="crew-segments-card-list" className="flex flex-col gap-3">
      {segments.map((segment) => (
        <CrewSegmentsCard key={segment.id} segment={segment} />
      ))}
    </div>
  )
}

function CrewSegmentsCard({ segment }: { segment: CrewSegmentRow }) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid={`crew-segments-card-${segment.id}`}
      data-segment-id={segment.id}
    >
      <CardContent className="flex items-start justify-between gap-3 py-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            href={`${crewPath()}?segment=${segment.id}`}
            className="hover:text-foreground truncate text-base font-semibold"
            title={segment.name}
            data-testid={`crew-segments-card-name-${segment.id}`}
          >
            {segment.name}
          </Link>
          <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
            <span
              data-testid={`crew-segments-card-members-${segment.id}`}
              className="tabular-nums"
            >
              {t("crew_segments.manage.card.members_count", {
                count: segment.members_count,
              })}
            </span>
            <span>
              {t("crew_segments.manage.card.created_by", {
                name:
                  segment.created_by?.name ??
                  t("crew_segments.manage.created_by_unknown"),
              })}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <SegmentRenamePopover segment={segment} />
          <SegmentDeleteDialog segment={segment} />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Empty-state card — UX Spec §2.3. `Bookmark` lucide icon + heading +
 * helper body + CTA back to /crew (where the user composes filters
 * before saving).
 */
function CrewSegmentsEmptyCard() {
  const { t } = useTranslation()

  return (
    <Card data-testid="crew-segments-empty">
      <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
        <Bookmark className="text-muted-foreground size-8" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {t("crew_segments.manage.empty_heading")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("crew_segments.manage.empty_body")}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href={crewPath()} data-testid="crew-segments-empty-cta">
            {t("crew_segments.manage.empty_cta")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Single segment row. Name links to the roster pre-filtered with the
 * segment applied — UX Spec §2.3 ("Click → applies to roster
 * (navigate /crew?segment=<id>)").
 */
function CrewSegmentsRow({ segment }: { segment: CrewSegmentRow }) {
  const { t } = useTranslation()

  return (
    <TableRow
      data-testid={`crew-segments-row-${segment.id}`}
      data-segment-id={segment.id}
    >
      <TableCell>
        <Link
          // js-routes helper — never hardcode the URL.
          href={`${crewPath()}?segment=${segment.id}`}
          className="hover:text-foreground font-medium"
          title={segment.name}
          data-testid={`crew-segments-row-name-${segment.id}`}
        >
          <span className="block max-w-[24ch] truncate">{segment.name}</span>
        </Link>
      </TableCell>
      <TableCell
        className="text-right tabular-nums"
        data-testid={`crew-segments-row-members-${segment.id}`}
      >
        {/* `members_count` — LQE-LOCKED wire key (plural-with-s). */}
        {segment.members_count}
      </TableCell>
      <TableCell className="text-muted-foreground hidden text-sm md:table-cell">
        <RulesSummary
          rules={segment.rules}
          masterclassLabel={segment.masterclass_completion_label}
        />
      </TableCell>
      <TableCell className="text-muted-foreground hidden text-sm lg:table-cell">
        {segment.created_by?.name ??
          t("crew_segments.manage.created_by_unknown")}
      </TableCell>
      <TableCell
        className="text-muted-foreground hidden text-sm md:table-cell"
        title={segment.created_at}
      >
        <SegmentRelativeDate iso={segment.created_at} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <SegmentRenamePopover segment={segment} />
          <SegmentDeleteDialog segment={segment} />
        </div>
      </TableCell>
    </TableRow>
  )
}

/**
 * Renders a one-line summary of the segment's rules ("Location: Paris,
 * FR · Tags: beauty, lifestyle"). Empty rules → "No rules (empty
 * segment)" per UX Spec §3 / PRD §AC.
 *
 * OKS-58 — When the segment carries a masterclass-completion rule, the
 * summary appends one clause:
 *   - `Has completed: <title>`         (state: published)
 *   - `Has not completed: <title>`     (state: published)
 *   - `Has completed: <title> (Unpublished)`         (state: draft)
 *   - `Has not completed: <title> (Unpublished)`     (state: draft)
 *   - `Has completed: (Deleted masterclass)`         (state: deleted)
 *   - `Has not completed: (Deleted masterclass)`     (state: deleted)
 *
 * The `(Unpublished)` / `(Deleted masterclass)` literals are locked
 * bilateral with LQE contract spec.
 */
function RulesSummary({
  rules,
  masterclassLabel,
}: {
  rules: CrewSegmentRules
  masterclassLabel: MasterclassCompletionLabel | null
}) {
  const { t } = useTranslation()

  const parts: string[] = []

  if (rules.location)
    parts.push(
      t("crew_segments.manage.rules_summary.location", {
        value: rules.location,
      }),
    )

  if (
    typeof rules.followers_min === "number" &&
    typeof rules.followers_max === "number"
  ) {
    parts.push(
      t("crew_segments.manage.rules_summary.followers_range", {
        min: rules.followers_min,
        max: rules.followers_max,
      }),
    )
  } else if (typeof rules.followers_min === "number") {
    parts.push(
      t("crew_segments.manage.rules_summary.followers_min", {
        value: rules.followers_min,
      }),
    )
  } else if (typeof rules.followers_max === "number") {
    parts.push(
      t("crew_segments.manage.rules_summary.followers_max", {
        value: rules.followers_max,
      }),
    )
  }

  if (rules.tags.length > 0) {
    parts.push(
      t("crew_segments.manage.rules_summary.tags", {
        tags: rules.tags.join(", "),
      }),
    )
  }

  if (rules.joined_from && rules.joined_to) {
    parts.push(
      t("crew_segments.manage.rules_summary.joined_range", {
        from: rules.joined_from,
        to: rules.joined_to,
      }),
    )
  } else if (rules.joined_from) {
    parts.push(
      t("crew_segments.manage.rules_summary.joined_from", {
        value: rules.joined_from,
      }),
    )
  } else if (rules.joined_to) {
    parts.push(
      t("crew_segments.manage.rules_summary.joined_to", {
        value: rules.joined_to,
      }),
    )
  }

  // OKS-58 — Masterclass-completion clause. The rule + label travel
  // together; if either is missing, the clause skips (zero-noise).
  // Three branches drive the literal output per the bilateral LQE
  // contract; the `(Unpublished)` / `(Deleted masterclass)` suffixes
  // are the locked literals.
  if (rules.masterclass_completion_op && masterclassLabel) {
    const op = rules.masterclass_completion_op
    if (masterclassLabel.state === "deleted") {
      // No title to render — fall back to the deleted-literal clause.
      const key =
        op === "is_completed"
          ? "crew_segments.manage.rules_summary.masterclass_deleted"
          : "crew_segments.manage.rules_summary.masterclass_deleted_not"
      parts.push(t(key))
    } else {
      const baseKey =
        op === "is_completed"
          ? "crew_segments.manage.rules_summary.masterclass_is_completed"
          : "crew_segments.manage.rules_summary.masterclass_is_not_completed"
      const base = t(baseKey, { title: masterclassLabel.title ?? "" })
      if (masterclassLabel.state === "draft") {
        parts.push(
          `${base} ${t("crew_segments.manage.rules_summary.masterclass_suffix_unpublished")}`,
        )
      } else {
        parts.push(base)
      }
    }
  }

  if (parts.length === 0) {
    return <span>{t("crew_segments.manage.rules_summary.none")}</span>
  }

  return <span className="truncate">{parts.join(" · ")}</span>
}

// Re-export crewSegmentsPath usage so the lint rule "must use js-routes"
// is satisfied even though the manage page itself doesn't call it
// directly (rename popover + delete dialog do).
void crewSegmentsPath
